import { useState } from "react";
import { Star, Quote, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "../Reveal";
import { api, errMsg } from "../../lib/api";

const Stars = ({ n }) => (
  <div className="flex gap-1" dir="ltr">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= n ? "text-yellow-400" : "text-zinc-700"}`} fill="currentColor" />
    ))}
  </div>
);

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#28282D] rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-yellow-400 outline-none transition-colors";

export const Reviews = ({ reviews }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("فضلًا أدخل الاسم والتقييم");
      return;
    }
    setSending(true);
    try {
      await api.post("/reviews", form);
      toast.success("شكرًا لك! سيظهر تقييمك بعد المراجعة");
      setForm({ name: "", rating: 5, text: "" });
      setShowForm(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 bg-[#0d0d0f] border-y border-[#1c1c20]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading kicker="آراء العملاء" title="ماذا يقول عملاؤنا؟" />
            <button
              data-testid="review-add-btn"
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 border border-yellow-400/50 text-yellow-400 font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-yellow-400/10 transition-colors"
            >
              {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showForm ? "إغلاق" : "شاركنا تجربتك"}
            </button>
          </div>
        </Reveal>

        {showForm && (
          <Reveal>
            <form data-testid="review-form" onSubmit={submit} className="mt-8 bg-[#121214] border border-[#28282D] rounded-xl p-6 grid sm:grid-cols-2 gap-4">
              <input
                data-testid="review-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="اسمك"
                className={inputCls}
              />
              <select
                data-testid="review-rating-select"
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                className={inputCls}
              >
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>{r} نجوم</option>
                ))}
              </select>
              <textarea
                data-testid="review-text-input"
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="اكتب تجربتك مع نجم الإطارات..."
                rows={3}
                className={`${inputCls} sm:col-span-2`}
              />
              <button
                data-testid="review-submit-btn"
                disabled={sending}
                className="sm:col-span-2 bg-yellow-400 text-black font-bold px-6 py-3 rounded-full hover:bg-yellow-300 transition-colors disabled:opacity-50"
              >
                {sending ? "جارٍ الإرسال..." : "إرسال التقييم"}
              </button>
            </form>
          </Reveal>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={(i % 2) * 0.1}>
              <div data-testid={`review-card-${r.id}`} className="relative h-full bg-[#121214] border border-[#28282D] rounded-xl p-6">
                <Quote className="absolute top-5 left-5 w-8 h-8 text-yellow-400/15" />
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center font-display font-bold text-yellow-400">
                      {r.name?.[0] || "ع"}
                    </span>
                    <span className="font-bold text-white text-sm">{r.name}</span>
                  </div>
                  <Stars n={r.rating} />
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{r.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
